import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth, dashboardPath } from "@/context/AuthContext";
import { PHONE, PHONE_TEL } from "@/lib/api";

const NAV = [
  { to: "/services", label: "Entretien" },
  { to: "/installation", label: "Installation" },
  { to: "/blog", label: "Blog" },
  { to: "/conseils", label: "Conseils" },
  { to: "/pro", label: "Professionnels" },
];

export function PublicHeader() {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-50 cl-glass border-b border-slate-200">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Logo size="h-12 sm:h-14" />
        <nav className="hidden items-center gap-8 md:flex">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`} className="text-sm font-medium text-slate-600 transition-colors hover:text-[#263871]">
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden items-center gap-4 md:flex">
          <a href={`tel:${PHONE_TEL}`} data-testid="header-phone" className="flex items-center gap-2 text-sm font-semibold text-[#263871] hover:text-[#1D9BF0]">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E0F2FE]"><Phone className="h-4 w-4 text-[#1D9BF0]" /></span>
            {PHONE}
          </a>
          {user ? (
            <Button data-testid="header-dashboard-btn" onClick={() => navigate(dashboardPath(user.role))} className="rounded-full bg-[#263871] px-5 hover:bg-[#35508F]">
              <User className="mr-1.5 h-4 w-4" />Mon espace
            </Button>
          ) : (
            <>
              <Button data-testid="header-client-space" variant="outline" onClick={() => navigate("/connexion")} className="rounded-full border-2 px-5">
                <User className="mr-1.5 h-4 w-4" />Espace client
              </Button>
              <Button data-testid="header-register-btn" onClick={() => navigate("/inscription")} className="rounded-full bg-[#1D9BF0] px-5 hover:bg-[#57A4FF]">
                Réserver
              </Button>
            </>
          )}
        </div>
        <div className="flex items-center gap-1.5 md:hidden">
          <a href={`tel:${PHONE_TEL}`} data-testid="header-phone-mobile" className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E0F2FE]"><Phone className="h-4 w-4 text-[#1D9BF0]" /></a>
          <button data-testid="header-client-space-mobile" onClick={() => navigate(user ? dashboardPath(user.role) : "/connexion")} className="flex h-9 w-9 items-center justify-center rounded-full bg-[#263871] text-white" aria-label="Espace client"><User className="h-4 w-4" /></button>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-1 text-[#263871]">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 bg-white px-4 py-4 md:hidden">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block py-2.5 font-medium text-slate-700">
              {n.label}
            </Link>
          ))}
          <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 py-2.5 font-semibold text-[#263871]"><Phone className="h-4 w-4 text-[#1D9BF0]" />{PHONE}</a>
          <div className="mt-3 flex flex-col gap-2">
            {user ? (
              <Button data-testid="menu-dashboard-btn" onClick={() => { setOpen(false); navigate(dashboardPath(user.role)); }} className="w-full rounded-full bg-[#263871]"><User className="mr-1.5 h-4 w-4" />Mon espace</Button>
            ) : (
              <>
                <Button data-testid="menu-client-space" variant="outline" onClick={() => { setOpen(false); navigate("/connexion"); }} className="w-full rounded-full border-2"><User className="mr-1.5 h-4 w-4" />Espace client</Button>
                <Button data-testid="menu-register-btn" onClick={() => { setOpen(false); navigate("/inscription"); }} className="w-full rounded-full bg-[#1D9BF0]">Réserver</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="cl-gradient mt-20 text-white">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4 lg:px-8">
        <div className="md:col-span-2">
          <Logo tone="dark" size="h-12 sm:h-14" />
          <p className="mt-4 text-sky-200">Pour ma clim c'est ici.</p>
          <p className="mt-3 max-w-md text-sm text-sky-100/80">
            La plateforme qui connecte particuliers et professionnels avec des techniciens certifiés pour l'entretien et l'installation de climatisation.
          </p>
        </div>
        <div>
          <h4 className="font-semibold">Services</h4>
          <ul className="mt-4 space-y-2 text-sm text-sky-100/80">
            <li><Link to="/services">Entretien clim</Link></li>
            <li><Link to="/services">Nettoyage</Link></li>
            <li><Link to="/services">Installation</Link></li>
            <li><Link to="/services">Contrats</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold">Rejoindre</h4>
          <ul className="mt-4 space-y-2 text-sm text-sky-100/80">
            <li><Link to="/rejoindre">Devenir technicien d'entretien</Link></li>
            <li><Link to="/rejoindre">Devenir installateur</Link></li>
            <li><Link to="/conseils">Base de conseils</Link></li>
            <li><Link to="/blog">Blog</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-sky-100/70 sm:flex-row sm:px-6 lg:px-8">
          <span>© {new Date().getFullYear()} Climateasy — Tous droits réservés.</span>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
            <Link to="/mentions-legales" data-testid="footer-mentions" className="hover:text-white">Mentions légales</Link>
            <Link to="/cgv" data-testid="footer-cgv" className="hover:text-white">CGV</Link>
            <Link to="/confidentialite" data-testid="footer-confidentialite" className="hover:text-white">Politique de confidentialité</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function BackgroundWaves() {
  const path = "M0,160 C240,100 480,220 720,160 C960,100 1200,220 1440,160 L1440,400 L0,400 Z";
  return (
    <div className="cl-waves" aria-hidden="true">
      <svg className="cl-wave-a" viewBox="0 0 1440 400" preserveAspectRatio="none"><path d={path} fill="currentColor" /></svg>
      <svg className="cl-wave-b" viewBox="0 0 1440 400" preserveAspectRatio="none"><path d={path} fill="currentColor" /></svg>
      <svg className="cl-wave-c" viewBox="0 0 1440 400" preserveAspectRatio="none"><path d={path} fill="currentColor" /></svg>
    </div>
  );
}

export function PublicLayout({ children }) {
  return (
    <div className="relative flex min-h-screen flex-col">
      <BackgroundWaves />
      <div className="relative z-10 flex min-h-screen flex-col">
        <PublicHeader />
        <main className="flex-1">{children}</main>
        <Footer />
      </div>
    </div>
  );
}
